export type FieldType = 'text' | 'date' | 'number' | 'boolean' | 'optionSet' | 'fk' | 'textarea';

export interface ColumnConfig {
  key: string;
  label: string;
  type: FieldType;
  targetEntity?: string;
  optionSetKey?: string;
}

export interface FormFieldConfig {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  targetEntity?: string;
  optionSetKey?: string;
  /** Groups consecutive fields under a collapsible section heading */
  section?: string;
  /** Sub-heading within a section (e.g. per note-pair label) */
  sectionLabel?: string;
}

export interface RelatedEntityConfig {
  entityName: string;
  displayName: string;
  foreignKey: string;
}

export interface EntityConfig {
  logicalName: string;
  displayName: string;
  pluralName: string;
  primaryKeyField: string;
  category: 'clients' | 'advisory' | 'products' | 'activities' | 'fees' | 'people' | 'other' | 'admin';
  listColumns: ColumnConfig[];
  formFields: FormFieldConfig[];
  nameField: string;
  relatedEntities?: RelatedEntityConfig[];
}

// ---------------------------------------------------------------------------
// Helper to build a simple lookup entity config (just a name field)
// ---------------------------------------------------------------------------
function simpleLookup(
  logicalName: string,
  displayName: string,
  pluralName: string,
  primaryKeyField: string,
  category: EntityConfig['category'],
  nameField = 't4a_name',
): EntityConfig {
  return {
    logicalName,
    displayName,
    pluralName,
    primaryKeyField,
    category,
    nameField,
    listColumns: [
      { key: nameField, label: 'Name', type: 'text' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
      { key: 'createdon', label: 'Created On', type: 'date' },
    ],
    formFields: [
      { key: nameField, label: 'Name', type: 'text', required: true },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  };
}

// ---------------------------------------------------------------------------
// Entity configs
// ---------------------------------------------------------------------------
const entityConfigs: EntityConfig[] = [
  // =========================================================================
  // CLIENTS
  // =========================================================================
  {
    logicalName: 'account',
    displayName: 'Account',
    pluralName: 'Accounts',
    primaryKeyField: 'accountid',
    category: 'clients',
    nameField: 'name',
    listColumns: [
      { key: 'name', label: 'Account Name', type: 'text' },
      { key: 'emailaddress1', label: 'Email', type: 'text' },
      { key: 't4a_accountstatus', label: 'Account Status', type: 'optionSet', optionSetKey: 'accountStatus' },
      { key: 't4a_adviserid', label: 'Adviser', type: 'fk', targetEntity: 'systemuser' },
      { key: 't4a_totalassets', label: 'Total Assets', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 'name', label: 'Account Name', type: 'text', required: true },
      { key: 'emailaddress1', label: 'Email', type: 'text' },
      { key: 'telephone1', label: 'Phone', type: 'text' },
      { key: 't4a_accountstatus', label: 'Account Status', type: 'optionSet', optionSetKey: 'accountStatus' },
      { key: 't4a_accounttype', label: 'Account Type', type: 'optionSet', optionSetKey: 'accountType' },
      { key: 't4a_adviserid', label: 'Adviser', type: 'fk', targetEntity: 'systemuser' },
      { key: 't4a_totalassets', label: 'Total Assets', type: 'number' },
      { key: 't4a_reviewfrequency', label: 'Review Frequency', type: 'optionSet', optionSetKey: 'reviewFrequency' },
      { key: 'address1_line1', label: 'Address Line 1', type: 'text' },
      { key: 'address1_city', label: 'City', type: 'text' },
      { key: 'address1_postalcode', label: 'Postcode', type: 'text' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    relatedEntities: [
      { entityName: 'contact', displayName: 'Contacts', foreignKey: 'accountid' },
      { entityName: 't4a_review', displayName: 'Reviews', foreignKey: 't4a_accountid' },
      { entityName: 't4a_curoholding', displayName: 'Holdings', foreignKey: 't4a_accountid' },
      { entityName: 't4a_curorecommendation', displayName: 'Recommendations', foreignKey: 't4a_accountid' },
      { entityName: 'task', displayName: 'Tasks', foreignKey: 't4a_accountid' },
      { entityName: 't4a_planningobjective', displayName: 'Objectives', foreignKey: 't4a_accountid' },
      { entityName: 't4a_atr', displayName: 'ATRs', foreignKey: 't4a_accountid' },
      { entityName: 't4a_asset', displayName: 'Assets', foreignKey: 't4a_accountid' },
      { entityName: 't4a_liabilities', displayName: 'Liabilities', foreignKey: 't4a_accountid' },
      { entityName: 't4a_income', displayName: 'Income', foreignKey: 't4a_accountid' },
      { entityName: 't4a_expenditure', displayName: 'Expenditure', foreignKey: 't4a_accountid' },
      { entityName: 't4a_beneficiary', displayName: 'Beneficiaries', foreignKey: 't4a_accountid' },
      { entityName: 't4a_gift', displayName: 'Gifts', foreignKey: 't4a_accountid' },
    ],
  },
  {
    logicalName: 'contact',
    displayName: 'Contact',
    pluralName: 'Contacts',
    primaryKeyField: 'contactid',
    category: 'clients',
    nameField: 'fullname',
    listColumns: [
      { key: 'fullname', label: 'Full Name', type: 'text' },
      { key: 'emailaddress1', label: 'Email', type: 'text' },
      { key: 'mobilephone', label: 'Mobile', type: 'text' },
      { key: 'birthdate', label: 'Date of Birth', type: 'date' },
      { key: 't4a_contactstatus', label: 'Contact Status', type: 'optionSet', optionSetKey: 'contactStatus' },
      { key: 'accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
    ],
    formFields: [
      { key: 't4a_title', label: 'Title', type: 'optionSet', optionSetKey: 'title' },
      { key: 'firstname', label: 'First Name', type: 'text', required: true },
      { key: 'lastname', label: 'Last Name', type: 'text', required: true },
      { key: 'emailaddress1', label: 'Email', type: 'text' },
      { key: 'mobilephone', label: 'Mobile Phone', type: 'text' },
      { key: 'telephone1', label: 'Home Phone', type: 'text' },
      { key: 'birthdate', label: 'Date of Birth', type: 'date' },
      { key: 'gendercode', label: 'Gender', type: 'optionSet', optionSetKey: 'genderCode' },
      { key: 'familystatuscode', label: 'Family Status', type: 'optionSet', optionSetKey: 'familyStatus' },
      { key: 't4a_contactstatus', label: 'Contact Status', type: 'optionSet', optionSetKey: 'contactStatus' },
      { key: 'accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 'jobtitle', label: 'Job Title', type: 'text' },
      { key: 't4a_nationalinsurancenumber', label: 'NI Number', type: 'text' },
      { key: 'address1_line1', label: 'Address Line 1', type: 'text' },
      { key: 'address1_postalcode', label: 'Postcode', type: 'text' },
    ],
    relatedEntities: [
      { entityName: 't4a_review', displayName: 'Reviews', foreignKey: 't4a_contactid' },
      { entityName: 't4a_curoholding', displayName: 'Holdings', foreignKey: 't4a_contactid' },
      { entityName: 't4a_curorecommendation', displayName: 'Recommendations', foreignKey: 't4a_contactid' },
      { entityName: 't4a_atr', displayName: 'ATRs', foreignKey: 't4a_contactid' },
      { entityName: 'task', displayName: 'Tasks', foreignKey: 't4a_contactid' },
    ],
  },
  {
    logicalName: 'lead',
    displayName: 'Lead',
    pluralName: 'Leads',
    primaryKeyField: 'leadid',
    category: 'clients',
    nameField: 'fullname',
    listColumns: [
      { key: 'firstname', label: 'First Name', type: 'text' },
      { key: 'lastname', label: 'Last Name', type: 'text' },
      { key: 'emailaddress1', label: 'Email', type: 'text' },
      { key: 't4a_leadsource', label: 'Lead Source', type: 'optionSet', optionSetKey: 'leadSource' },
      { key: 'statuscode', label: 'Status', type: 'optionSet', optionSetKey: 'statuscode' },
      { key: 't4a_adviserid', label: 'Adviser', type: 'fk', targetEntity: 'systemuser' },
    ],
    formFields: [
      { key: 'firstname', label: 'First Name', type: 'text', required: true },
      { key: 'lastname', label: 'Last Name', type: 'text', required: true },
      { key: 'emailaddress1', label: 'Email', type: 'text' },
      { key: 'mobilephone', label: 'Mobile Phone', type: 'text' },
      { key: 'telephone1', label: 'Phone', type: 'text' },
      { key: 't4a_leadsource', label: 'Lead Source', type: 'optionSet', optionSetKey: 'leadSource' },
      { key: 't4a_leadtype', label: 'Lead Type', type: 'optionSet', optionSetKey: 'leadType' },
      { key: 't4a_adviserid', label: 'Adviser', type: 'fk', targetEntity: 'systemuser' },
      { key: 'companyname', label: 'Company Name', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'statuscode', label: 'Status', type: 'optionSet', optionSetKey: 'statuscode' },
    ],
  },

  // =========================================================================
  // ADVISORY
  // =========================================================================
  {
    logicalName: 't4a_review',
    displayName: 'Review',
    pluralName: 'Reviews',
    primaryKeyField: 't4a_reviewid',
    category: 'advisory',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_reviewdate', label: 'Review Date', type: 'date' },
      { key: 'statuscode', label: 'Status', type: 'optionSet', optionSetKey: 'reviewStatus' },
      { key: 't4a_adviserid', label: 'Adviser', type: 'fk', targetEntity: 'systemuser' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', required: true, targetEntity: 'account' },
      { key: 't4a_reviewdate', label: 'Review Date', type: 'date', required: true },
      { key: 't4a_adviserid', label: 'Adviser', type: 'fk', targetEntity: 'systemuser' },
      { key: 't4a_reviewfrequency', label: 'Review Frequency', type: 'optionSet', optionSetKey: 'reviewFrequency' },
      { key: 'statuscode', label: 'Status', type: 'optionSet', optionSetKey: 'reviewStatus' },
      // Handover Notes — 10 sections, each with an amends textarea + reviewed dropdown
      { key: 't4a_personalamends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Personal details' },
      { key: 't4a_personaldetails', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Personal details' },
      { key: 't4a_incomeamends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Income & Expenditure' },
      { key: 't4a_incomeexpenditure', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Income & Expenditure' },
      { key: 't4a_protectionamends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Protection' },
      { key: 't4a_protectionhealth', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Protection' },
      { key: 't4a_retirementamends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Retirement planning' },
      { key: 't4a_retirementplanning', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Retirement planning' },
      { key: 't4a_taxationamends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Taxation' },
      { key: 't4a_taxation', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Taxation' },
      { key: 't4a_loanamends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Loans & Liabilities' },
      { key: 't4a_loansliabilities', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Loans & Liabilities' },
      { key: 't4a_atramends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Attitude to risk' },
      { key: 't4a_attitudetorisk', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Attitude to risk' },
      { key: 't4a_investmentamends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Saving & Investment' },
      { key: 't4a_savinginvestments', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Saving & Investment' },
      { key: 't4a_otheramends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Other business' },
      { key: 't4a_other', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Other business' },
      { key: 't4a_estateamends', label: 'Amends', type: 'textarea', section: 'Handover Notes', sectionLabel: 'Estate planning' },
      { key: 't4a_estateplanning', label: 'Reviewed', type: 'optionSet', optionSetKey: 'notesReviewed', section: 'Handover Notes', sectionLabel: 'Estate planning' },
    ],
    relatedEntities: [
      { entityName: 't4a_curorecommendation', displayName: 'Recommendations', foreignKey: 't4a_reviewid' },
      { entityName: 't4a_planningobjective', displayName: 'Objectives', foreignKey: 't4a_reviewid' },
      { entityName: 'task', displayName: 'Tasks', foreignKey: 'regardingobjectid' },
      { entityName: 't4a_milestone', displayName: 'Projects', foreignKey: 't4a_reviewid' },
    ],
  },
  {
    logicalName: 't4a_planningobjective',
    displayName: 'Planning Objective',
    pluralName: 'Planning Objectives',
    primaryKeyField: 't4a_planningobjectiveid',
    category: 'advisory',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_planningobjectivetype', label: 'Type', type: 'optionSet', optionSetKey: 'planningObjectiveType' },
      { key: 't4a_targetfundtodaysterms', label: 'Target Fund (Today)', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', required: true, targetEntity: 'account' },
      { key: 't4a_planningobjectivetype', label: 'Type', type: 'optionSet', optionSetKey: 'planningObjectiveType' },
      { key: 't4a_targetfundtodaysterms', label: 'Target Fund (Today)', type: 'number' },
      { key: 't4a_targetdate', label: 'Target Date', type: 'date' },
      { key: 't4a_planningpriority', label: 'Priority', type: 'optionSet', optionSetKey: 'planningPriority' },
      { key: 't4a_notes', label: 'Notes', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_curorecommendation',
    displayName: 'Recommendation',
    pluralName: 'Recommendations',
    primaryKeyField: 't4a_curorecommendationid',
    category: 'advisory',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_reviewid', label: 'Review', type: 'fk', targetEntity: 't4a_review' },
      { key: 't4a_recommendationstatus', label: 'Status', type: 'optionSet', optionSetKey: 'recommendationStatus' },
      { key: 't4a_totalcontribution', label: 'Total Contribution', type: 'number' },
      { key: 'createdon', label: 'Created On', type: 'date' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_reviewid', label: 'Review', type: 'fk', required: true, targetEntity: 't4a_review' },
      { key: 't4a_recommendationstatus', label: 'Status', type: 'optionSet', optionSetKey: 'recommendationStatus' },
      { key: 't4a_totalcontribution', label: 'Total Contribution', type: 'number' },
      { key: 't4a_totaltransfer', label: 'Total Transfer', type: 'number' },
      { key: 't4a_totalwithdrawal', label: 'Total Withdrawal', type: 'number' },
      { key: 't4a_notes', label: 'Notes', type: 'textarea' },
    ],
    relatedEntities: [
      { entityName: 't4a_feeprofile', displayName: 'Fee Profiles', foreignKey: 't4a_recommendationid' },
      { entityName: 't4a_milestone', displayName: 'Projects', foreignKey: 't4a_recommendationid' },
    ],
  },
  {
    logicalName: 't4a_atr',
    displayName: 'ATR',
    pluralName: 'ATRs',
    primaryKeyField: 't4a_atrid',
    category: 'advisory',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_contactid', label: 'Contact', type: 'fk', targetEntity: 'contact' },
      { key: 't4a_atrscore', label: 'ATR Score', type: 'number' },
      { key: 't4a_datecompleted', label: 'Date Completed', type: 'date' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_contactid', label: 'Contact', type: 'fk', required: true, targetEntity: 'contact' },
      { key: 't4a_atrscore', label: 'ATR Score', type: 'number' },
      { key: 't4a_datecompleted', label: 'Date Completed', type: 'date' },
      { key: 't4a_riskprofile', label: 'Risk Profile', type: 'optionSet', optionSetKey: 'riskProfile' },
      { key: 't4a_capacityforloss', label: 'Capacity for Loss', type: 'text' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },

  // =========================================================================
  // PRODUCTS
  // =========================================================================
  {
    logicalName: 't4a_curoholding',
    displayName: 'Holding',
    pluralName: 'Holdings',
    primaryKeyField: 't4a_curoholdingid',
    category: 'products',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_curoproductid', label: 'Product', type: 'fk', targetEntity: 't4a_curoproduct' },
      { key: 't4a_currentvalue', label: 'Current Value', type: 'number' },
      { key: 't4a_planstatus', label: 'Plan Status', type: 'optionSet', optionSetKey: 'planStatus' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', required: true, targetEntity: 'account' },
      { key: 't4a_curoproductid', label: 'Product', type: 'fk', targetEntity: 't4a_curoproduct' },
      { key: 't4a_providerid', label: 'Provider', type: 'fk', targetEntity: 't4a_provider' },
      { key: 't4a_currentvalue', label: 'Current Value', type: 'number' },
      { key: 't4a_startdate', label: 'Start Date', type: 'date' },
      { key: 't4a_planstatus', label: 'Plan Status', type: 'optionSet', optionSetKey: 'planStatus' },
      { key: 't4a_policyreference', label: 'Policy Reference', type: 'text' },
      { key: 't4a_modelportfolioid', label: 'Model Portfolio', type: 'fk', targetEntity: 't4a_modelportfolio' },
      { key: 't4a_notes', label: 'Notes', type: 'textarea' },
    ],
    relatedEntities: [
      { entityName: 't4a_feeprofile', displayName: 'Fee Profiles', foreignKey: 't4a_curoholdingid' },
      { entityName: 't4a_curorecommendation', displayName: 'Recommendations', foreignKey: 't4a_curoholdingid' },
    ],
  },
  {
    logicalName: 't4a_curoproduct',
    displayName: 'Product',
    pluralName: 'Products',
    primaryKeyField: 't4a_curoproductid',
    category: 'products',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_reportacronym', label: 'Report Acronym', type: 'text' },
      { key: 't4a_productbasis', label: 'Product Basis', type: 'optionSet', optionSetKey: 'productBasis' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_reportacronym', label: 'Report Acronym', type: 'text' },
      { key: 't4a_productbasis', label: 'Product Basis', type: 'optionSet', optionSetKey: 'productBasis' },
      { key: 't4a_taxwrapper', label: 'Tax Wrapper', type: 'text' },
      { key: 't4a_description', label: 'Description', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_tranche',
    displayName: 'Tranche',
    pluralName: 'Tranches',
    primaryKeyField: 't4a_trancheid',
    category: 'products',
    nameField: 't4a_retailname',
    listColumns: [
      { key: 't4a_retailname', label: 'Retail Name', type: 'text' },
      { key: 't4a_providerid', label: 'Provider', type: 'fk', targetEntity: 't4a_provider' },
      { key: 't4a_productid', label: 'Product', type: 'fk', targetEntity: 't4a_curoproduct' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_retailname', label: 'Retail Name', type: 'text', required: true },
      { key: 't4a_providerid', label: 'Provider', type: 'fk', required: true, targetEntity: 't4a_provider' },
      { key: 't4a_productid', label: 'Product', type: 'fk', required: true, targetEntity: 't4a_curoproduct' },
      { key: 't4a_sedolcode', label: 'SEDOL Code', type: 'text' },
      { key: 't4a_isincode', label: 'ISIN Code', type: 'text' },
      { key: 't4a_ongoingchargefigure', label: 'OCF (%)', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_provider',
    displayName: 'Provider',
    pluralName: 'Providers',
    primaryKeyField: 't4a_providerid',
    category: 'products',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_providertype', label: 'Provider Type', type: 'optionSet', optionSetKey: 'providerType' },
      { key: 't4a_panelprovider', label: 'Panel Provider', type: 'boolean' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_providertype', label: 'Provider Type', type: 'optionSet', optionSetKey: 'providerType' },
      { key: 't4a_panelprovider', label: 'Panel Provider', type: 'boolean' },
      { key: 't4a_website', label: 'Website', type: 'text' },
      { key: 't4a_telephone', label: 'Telephone', type: 'text' },
      { key: 't4a_email', label: 'Email', type: 'text' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_modelportfolio',
    displayName: 'Model Portfolio',
    pluralName: 'Model Portfolios',
    primaryKeyField: 't4a_modelportfolioid',
    category: 'products',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_riskprofile', label: 'Risk Profile', type: 'optionSet', optionSetKey: 'riskProfile' },
      { key: 't4a_fundmanagerid', label: 'Fund Manager', type: 'fk', targetEntity: 't4a_provider' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_riskprofile', label: 'Risk Profile', type: 'optionSet', optionSetKey: 'riskProfile' },
      { key: 't4a_fundmanagerid', label: 'Fund Manager', type: 'fk', targetEntity: 't4a_provider' },
      { key: 't4a_benchmarkindex', label: 'Benchmark Index', type: 'text' },
      { key: 't4a_ongoingchargefigure', label: 'OCF (%)', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },

  // =========================================================================
  // ACTIVITIES
  // =========================================================================
  {
    logicalName: 'task',
    displayName: 'Task',
    pluralName: 'Tasks',
    primaryKeyField: 'activityid',
    category: 'activities',
    nameField: 'subject',
    listColumns: [
      { key: 'subject', label: 'Subject', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 'scheduledend', label: 'Due Date', type: 'date' },
      { key: 'statuscode', label: 'Status', type: 'optionSet', optionSetKey: 'taskStatus' },
      { key: 'prioritycode', label: 'Priority', type: 'optionSet', optionSetKey: 'priorityCode' },
    ],
    formFields: [
      { key: 'subject', label: 'Subject', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 'scheduledstart', label: 'Start Date', type: 'date' },
      { key: 'scheduledend', label: 'Due Date', type: 'date' },
      { key: 'prioritycode', label: 'Priority', type: 'optionSet', optionSetKey: 'priorityCode' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'statuscode', label: 'Status', type: 'optionSet', optionSetKey: 'taskStatus' },
    ],
  },
  {
    logicalName: 'phonecall',
    displayName: 'Phone Call',
    pluralName: 'Phone Calls',
    primaryKeyField: 'activityid',
    category: 'activities',
    nameField: 'subject',
    listColumns: [
      { key: 'subject', label: 'Subject', type: 'text' },
      { key: 'scheduledend', label: 'Due Date', type: 'date' },
      { key: 'directioncode', label: 'Outgoing', type: 'boolean' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 'subject', label: 'Subject', type: 'text', required: true },
      { key: 'phonenumber', label: 'Phone Number', type: 'text' },
      { key: 'scheduledstart', label: 'Start Date', type: 'date' },
      { key: 'scheduledend', label: 'Due Date', type: 'date' },
      { key: 'directioncode', label: 'Outgoing', type: 'boolean' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_milestone',
    displayName: 'Milestone',
    pluralName: 'Milestones',
    primaryKeyField: 't4a_milestoneid',
    category: 'activities',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 'statuscode', label: 'Status', type: 'optionSet', optionSetKey: 'statuscode' },
      { key: 't4a_categoryid', label: 'Category', type: 'fk', targetEntity: 't4a_milestonecategory' },
      { key: 'createdon', label: 'Created On', type: 'date' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', required: true, targetEntity: 'account' },
      { key: 't4a_categoryid', label: 'Category', type: 'fk', targetEntity: 't4a_milestonecategory' },
      { key: 'statuscode', label: 'Status', type: 'optionSet', optionSetKey: 'statuscode' },
      { key: 't4a_duedate', label: 'Due Date', type: 'date' },
      { key: 't4a_notes', label: 'Notes', type: 'textarea' },
    ],
  },
  {
    logicalName: 't4a_milestonecategory',
    displayName: 'Milestone Category',
    pluralName: 'Milestone Categories',
    primaryKeyField: 't4a_milestonecategoryid',
    category: 'activities',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
      { key: 'createdon', label: 'Created On', type: 'date' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_milestoneevent',
    displayName: 'Milestone Event',
    pluralName: 'Milestone Events',
    primaryKeyField: 't4a_milestoneeventid',
    category: 'activities',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_order', label: 'Order', type: 'number' },
      { key: 't4a_milestonecategoryid', label: 'Category', type: 'fk', targetEntity: 't4a_milestonecategory' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_order', label: 'Order', type: 'number' },
      { key: 't4a_milestonecategoryid', label: 'Category', type: 'fk', required: true, targetEntity: 't4a_milestonecategory' },
      { key: 't4a_description', label: 'Description', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },

  // =========================================================================
  // FEES
  // =========================================================================
  {
    logicalName: 't4a_feecomponent',
    displayName: 'Fee Component',
    pluralName: 'Fee Components',
    primaryKeyField: 't4a_feecomponentid',
    category: 'fees',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_feetype', label: 'Fee Type', type: 'optionSet', optionSetKey: 'feeType' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_feetype', label: 'Fee Type', type: 'optionSet', optionSetKey: 'feeType' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 't4a_percentage', label: 'Percentage', type: 'number' },
      { key: 't4a_vatapplicable', label: 'VAT Applicable', type: 'boolean' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_feeprofile',
    displayName: 'Fee Profile',
    pluralName: 'Fee Profiles',
    primaryKeyField: 't4a_feeprofileid',
    category: 'fees',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_curoholdingid', label: 'Holding', type: 'fk', targetEntity: 't4a_curoholding' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_curoholdingid', label: 'Holding', type: 'fk', targetEntity: 't4a_curoholding' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 't4a_percentage', label: 'Percentage', type: 'number' },
      { key: 't4a_frequency', label: 'Frequency', type: 'optionSet', optionSetKey: 'frequency' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_incomeprofile',
    displayName: 'Income Profile',
    pluralName: 'Income Profiles',
    primaryKeyField: 't4a_incomeprofileid',
    category: 'fees',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_frequency', label: 'Frequency', type: 'optionSet', optionSetKey: 'frequency' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_frequency', label: 'Frequency', type: 'optionSet', optionSetKey: 'frequency' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 't4a_startdate', label: 'Start Date', type: 'date' },
      { key: 't4a_enddate', label: 'End Date', type: 'date' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },

  // =========================================================================
  // PEOPLE
  // =========================================================================
  {
    logicalName: 'systemuser',
    displayName: 'User',
    pluralName: 'Users',
    primaryKeyField: 'systemuserid',
    category: 'people',
    nameField: 'fullname',
    listColumns: [
      { key: 'fullname', label: 'Full Name', type: 'text' },
      { key: 'jobtitle', label: 'Job Title', type: 'text' },
      { key: 't4a_adviser', label: 'Adviser', type: 'boolean' },
      { key: 'businessunitid', label: 'Business Unit', type: 'fk', targetEntity: 'businessunit' },
      { key: 'internalemailaddress', label: 'Email', type: 'text' },
    ],
    formFields: [
      { key: 'firstname', label: 'First Name', type: 'text', required: true },
      { key: 'lastname', label: 'Last Name', type: 'text', required: true },
      { key: 'jobtitle', label: 'Job Title', type: 'text' },
      { key: 'internalemailaddress', label: 'Email', type: 'text' },
      { key: 't4a_adviser', label: 'Adviser', type: 'boolean' },
      { key: 'businessunitid', label: 'Business Unit', type: 'fk', targetEntity: 'businessunit' },
    ],
  },
  {
    logicalName: 'businessunit',
    displayName: 'Business Unit',
    pluralName: 'Business Units',
    primaryKeyField: 'businessunitid',
    category: 'people',
    nameField: 'name',
    listColumns: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'divisionname', label: 'Division', type: 'text' },
      { key: 'createdon', label: 'Created On', type: 'date' },
    ],
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'divisionname', label: 'Division', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
    ],
  },
  {
    logicalName: 'site',
    displayName: 'Site',
    pluralName: 'Sites',
    primaryKeyField: 'siteid',
    category: 'people',
    nameField: 'name',
    listColumns: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'createdon', label: 'Created On', type: 'date' },
    ],
    formFields: [
      { key: 'name', label: 'Name', type: 'text', required: true },
      { key: 'emailaddress', label: 'Email', type: 'text' },
    ],
  },
  {
    logicalName: 't4a_providercontact',
    displayName: 'Provider Contact',
    pluralName: 'Provider Contacts',
    primaryKeyField: 't4a_providercontactid',
    category: 'people',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_parentfirmid', label: 'Provider', type: 'fk', targetEntity: 't4a_provider' },
      { key: 't4a_jobtitle', label: 'Job Title', type: 'text' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_parentfirmid', label: 'Provider', type: 'fk', targetEntity: 't4a_provider' },
      { key: 't4a_jobtitle', label: 'Job Title', type: 'text' },
      { key: 't4a_email', label: 'Email', type: 'text' },
      { key: 't4a_telephone', label: 'Telephone', type: 'text' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },

  // =========================================================================
  // OTHER
  // =========================================================================
  {
    logicalName: 'transactioncurrency',
    displayName: 'Currency',
    pluralName: 'Currencies',
    primaryKeyField: 'transactioncurrencyid',
    category: 'other',
    nameField: 'currencyname',
    listColumns: [
      { key: 'currencyname', label: 'Currency Name', type: 'text' },
      { key: 'isocurrencycode', label: 'ISO Code', type: 'text' },
      { key: 'exchangerate', label: 'Exchange Rate', type: 'number' },
    ],
    formFields: [
      { key: 'currencyname', label: 'Currency Name', type: 'text', required: true },
      { key: 'isocurrencycode', label: 'ISO Code', type: 'text', required: true },
      { key: 'exchangerate', label: 'Exchange Rate', type: 'number' },
      { key: 'currencysymbol', label: 'Symbol', type: 'text' },
    ],
  },
  {
    logicalName: 'annotation',
    displayName: 'Note',
    pluralName: 'Notes',
    primaryKeyField: 'annotationid',
    category: 'other',
    nameField: 'subject',
    listColumns: [
      { key: 'subject', label: 'Subject', type: 'text' },
      { key: 'notetext', label: 'Note Text', type: 'text' },
      { key: 'createdon', label: 'Created On', type: 'date' },
    ],
    formFields: [
      { key: 'subject', label: 'Subject', type: 'text', required: true },
      { key: 'notetext', label: 'Note Text', type: 'textarea' },
    ],
  },
  {
    logicalName: 't4a_activitygroup',
    displayName: 'Activity Group',
    pluralName: 'Activity Groups',
    primaryKeyField: 't4a_activitygroupid',
    category: 'other',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_t4amaintained', label: 'Maintained', type: 'boolean' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_t4amaintained', label: 'Maintained', type: 'boolean' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_activitysubgroup',
    displayName: 'Activity Subgroup',
    pluralName: 'Activity Subgroups',
    primaryKeyField: 't4a_activitysubgroupid',
    category: 'other',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_description', label: 'Description', type: 'text' },
      { key: 't4a_subject', label: 'Subject', type: 'text' },
      { key: 't4a_notetext', label: 'Required Entity', type: 'text' },
      { key: 't4a_activitygroupid', label: 'Activity Group', type: 'fk', targetEntity: 't4a_activitygroup' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_description', label: 'Description', type: 'text' },
      { key: 't4a_subject', label: 'Subject', type: 'text' },
      { key: 't4a_notetext', label: 'Required Entity', type: 'text' },
      { key: 't4a_activitygroupid', label: 'Activity Group', type: 'fk', targetEntity: 't4a_activitygroup' },
      { key: 't4a_t4amaintained', label: 'Maintained', type: 'boolean' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_beneficiary',
    displayName: 'Beneficiary',
    pluralName: 'Beneficiaries',
    primaryKeyField: 't4a_beneficiaryid',
    category: 'other',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_percentage', label: 'Percentage', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_percentage', label: 'Percentage', type: 'number' },
      { key: 't4a_relationship', label: 'Relationship', type: 'text' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_asset',
    displayName: 'Asset',
    pluralName: 'Assets',
    primaryKeyField: 't4a_assetid',
    category: 'other',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_value', label: 'Value', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_value', label: 'Value', type: 'number' },
      { key: 't4a_description', label: 'Description', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_liabilities',
    displayName: 'Liability',
    pluralName: 'Liabilities',
    primaryKeyField: 't4a_liabilitiesid',
    category: 'other',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_value', label: 'Value', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_value', label: 'Value', type: 'number' },
      { key: 't4a_description', label: 'Description', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_gift',
    displayName: 'Gift',
    pluralName: 'Gifts',
    primaryKeyField: 't4a_giftid',
    category: 'other',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_value', label: 'Value', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_value', label: 'Value', type: 'number' },
      { key: 't4a_date', label: 'Date', type: 'date' },
      { key: 't4a_description', label: 'Description', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_income',
    displayName: 'Income',
    pluralName: 'Incomes',
    primaryKeyField: 't4a_incomeid',
    category: 'other',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 't4a_frequency', label: 'Frequency', type: 'optionSet', optionSetKey: 'frequency' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 't4a_frequency', label: 'Frequency', type: 'optionSet', optionSetKey: 'frequency' },
      { key: 't4a_description', label: 'Description', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  {
    logicalName: 't4a_expenditure',
    displayName: 'Expenditure',
    pluralName: 'Expenditures',
    primaryKeyField: 't4a_expenditureid',
    category: 'other',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 't4a_frequency', label: 'Frequency', type: 'optionSet', optionSetKey: 'frequency' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_accountid', label: 'Account', type: 'fk', targetEntity: 'account' },
      { key: 't4a_amount', label: 'Amount', type: 'number' },
      { key: 't4a_frequency', label: 'Frequency', type: 'optionSet', optionSetKey: 'frequency' },
      { key: 't4a_description', label: 'Description', type: 'textarea' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },

  // =========================================================================
  // GENERIC LOOKUPS (admin category)
  // =========================================================================
  simpleLookup('t4a_wrap', 'Wrap', 'Wraps', 't4a_wrapid', 'admin'),
  simpleLookup('t4a_groupscheme', 'Group Scheme', 'Group Schemes', 't4a_groupschemeid', 'admin'),
  simpleLookup('t4a_schemecategory', 'Scheme Category', 'Scheme Categories', 't4a_schemecategoryid', 'admin'),
  simpleLookup('t4a_recommendationtemplate', 'Recommendation Template', 'Recommendation Templates', 't4a_recommendationtemplateid', 'admin'),
  simpleLookup('t4a_eventattendee', 'Event Attendee', 'Event Attendees', 't4a_eventattendeeid', 'admin'),
  simpleLookup('campaign', 'Campaign', 'Campaigns', 'campaignid', 'admin', 'name'),
  simpleLookup('t4a_plangroup', 'Plan Group', 'Plan Groups', 't4a_plangroupid', 'admin'),
  simpleLookup('t4a_introducingfirm', 'Introducing Firm', 'Introducing Firms', 't4a_introducingfirmid', 'admin'),
  simpleLookup('t4a_introducer', 'Introducer', 'Introducers', 't4a_introducerid', 'admin'),
  simpleLookup('t4a_transferreason', 'Transfer Reason', 'Transfer Reasons', 't4a_transferreasonid', 'admin'),
  simpleLookup('t4a_feetemplate', 'Fee Template', 'Fee Templates', 't4a_feetemplateid', 'admin'),
  simpleLookup('t4a_advicetype', 'Advice Type', 'Advice Types', 't4a_advicetypeid', 'admin'),
  simpleLookup('t4a_will', 'Will', 'Wills', 't4a_willid', 'admin'),
  simpleLookup('t4a_powerofattorney', 'Power of Attorney', 'Powers of Attorney', 't4a_powerofattorneyid', 'admin'),
  {
    logicalName: 't4a_riskprofile',
    displayName: 'Risk Profile',
    pluralName: 'Risk Profiles',
    primaryKeyField: 't4a_riskprofileid',
    category: 'admin',
    nameField: 't4a_name',
    listColumns: [
      { key: 't4a_name', label: 'Name', type: 'text' },
      { key: 't4a_order', label: 'Score', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
    formFields: [
      { key: 't4a_name', label: 'Name', type: 'text', required: true },
      { key: 't4a_order', label: 'Score', type: 'number' },
      { key: 'statecode', label: 'Status', type: 'optionSet', optionSetKey: 'statecode' },
    ],
  },
  simpleLookup('t4a_lookup', 'Lookup', 'Lookups', 't4a_lookupid', 'admin'),
  simpleLookup('t4a_clienttype', 'Client Type', 'Client Types', 't4a_clienttypeid', 'admin'),
  simpleLookup('t4a_proposition', 'Proposition', 'Propositions', 't4a_propositionid', 'admin'),
  simpleLookup('t4a_advicecategory', 'Advice Category', 'Advice Categories', 't4a_advicecategoryid', 'admin'),
];

// ---------------------------------------------------------------------------
// Lookup maps
// ---------------------------------------------------------------------------
const configByLogicalName = new Map<string, EntityConfig>();
for (const cfg of entityConfigs) {
  configByLogicalName.set(cfg.logicalName, cfg);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getEntityConfig(logicalName: string): EntityConfig | undefined {
  return configByLogicalName.get(logicalName);
}

export function getAllEntityConfigs(): EntityConfig[] {
  return entityConfigs;
}

export function getEntityConfigsByCategory(): Record<string, EntityConfig[]> {
  const result: Record<string, EntityConfig[]> = {};
  for (const cfg of entityConfigs) {
    if (!result[cfg.category]) {
      result[cfg.category] = [];
    }
    result[cfg.category].push(cfg);
  }
  return result;
}
